import './PhotoGallery.scss';
import { Button, Col, Image, Modal, Card } from 'react-bootstrap';
import { COLORS_NAME } from '../../../config/colors';
import { shuffle } from 'lodash-es';
import axios from '../../../utils/axiosConfig';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import useBreakpoint, { SIZE_LG, SIZE_MD, SIZE_SM, SIZE_XL, SIZE_XS, SIZE_XXL } from '../../hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';
import { AGENTS_ENDPOINTS, INFLUENCERS_ENDPOINTS } from '../../../config/api';

const Div = ({ children, ...props }) => <div {...props}>{children}</div>;
function PhotoGallery({
  xs,
  sm,
  lg,
  xl,
  xxl,
  userId,
  extId,
  platform,
  username,
  headerMode,
  quantity,
  preloadedImages,
  analytics,
  clients
}) {
  const breakpoint = useBreakpoint();
  const [state, setState] = useState({
    media: [],
    activeMedia: [],
    loading: true,
    error: false,
    show: false,
    imgInFocus: ''
  });
  // Creates variables to know how many elements by row must be used
  const xsSize = 12 / xs;
  const smSize = sm ? 12 / sm : xsSize;
  const lgSize = lg ? (12 / lg) : smSize;
  const xlSize = xl ? (12 / xl) : lgSize;
  const xxlSize = xxl ? (12 / xxl) : xlSize;

  const { t } = useTranslation();

  useEffect(() => {
    let url = INFLUENCERS_ENDPOINTS.SOCIAL_PLATFORM_POSTS(platform, quantity);
    if (clients) {
      url = AGENTS_ENDPOINTS.SOCIAL_PLATFORM_POSTS(userId, platform, quantity);
    }

    if (userId) {
      url += `&user_id=${userId}`;
    } else if (extId) {
      url += `&ext_id=${extId}`;
    } else if (username) {
      url += `&username=${username}`;
    } else if (preloadedImages && preloadedImages.length) {
      return calculateActiveMedia(preloadedImages);
    } else {
      console.error('Error');
    }
    axios.get(url)
      .then(({ data }) => {
        const media = data.map(post => post.thumb_url);
        calculateActiveMedia(media);
      }).catch(() => {
        setState({
          ...state,
          loading: false,
          error: true
        });
      });
  }, []);

  useEffect(() => {
    const { media } = state;
    calculateActiveMedia(media);
  }, [breakpoint]);

  const shuffleImages = useCallback((media) => {
    const spaces = 9 - media.length;
    if (!spaces) shuffle(media);

    const layout = [...media, ...shuffle(COLORS_NAME).slice(0, spaces)];
    return shuffle(layout);
  }, []);

  const calculateActiveMedia = useCallback((media) => {
    if (media.length === 0) return;

    switch (breakpoint) {
      case SIZE_XS:
        if (headerMode) {
          const shuffled = shuffleImages(media);
          setState({
            ...state,
            media,
            activeMedia: [...shuffled.slice(0, 3)]
          });
        } else {
          setState({
            ...state,
            media,
            activeMedia: [...media.slice(0, 6)]
          });
        }
        break;
      case SIZE_SM:
      case SIZE_MD:
      case SIZE_LG:
      case SIZE_XL:
      case SIZE_XXL:
        if (headerMode) {
          const shuffled = shuffleImages(media);
          setState({
            ...state,
            media,
            activeMedia: shuffled
          });
        } else {
          setState({
            ...state,
            media,
            activeMedia: [...media.slice(0, 8)]
          });
        }
        break;
    }

  }, [state, breakpoint]);

  const renderImage = useCallback((image, platform) => {
    if (!image.includes('/')) {
      return <div className={classNames("color", image.toLowerCase())} />;
    }
    if (preloadedImages) { return <Image className="photo w-100" fluid src={image} onClick={handleShow} /> }
    switch (platform) {
      case 'instagram':
        return <Image className="photo w-100" fluid src={image} />;
      // TODO: implement tag for videos
      default: return null;
    }
  }, [state]);

  const handleShow = (e) => {
    setState({...state, show: true, imgInFocus: e.target.src})
  }

  const handleClose = () => setState({...state, show: false})

  const { activeMedia } = state;

  if(!analytics || (activeMedia.length > 0) ) {
    return (
      <Fragment>
        <div className={classNames("PhotoGallery", {
          headerMode,
          row: !headerMode
        })}>
          {
            activeMedia.map((image, index) => {
              const indexName = `img${index + 1}`;
              const ImageContainer = headerMode ? Div : Col;

              return (
                <ImageContainer
                  key={index}
                  className={classNames({
                    'mb-3': !headerMode,
                    [indexName]: headerMode
                  })}
                  xs={xsSize}
                  sm={smSize}
                  lg={lgSize}
                  xl={xlSize}
                  xxl={xxlSize}
                >
                  <div className={classNames('square', {
                    headerMode
                  })}>
                    {renderImage(image, platform)}
                  </div>
                </ImageContainer>
              );
            })
          }
        </div >
        <Modal show={state.show} onHide={handleClose}>
          <Modal.Header></Modal.Header>
          <Modal.Body>
            <img
              src={state.imgInFocus}
              className="img-fluid"
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="tertiary" onClick={handleClose}>
              {t('common.close')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Fragment>
    )
  }
  else {
    return(
      <Fragment>
        <Card className='card empty analytics'>
          {t('common.pages.analytics.samples_processing')}
        </Card>
      </Fragment>
    )
  }
}

PhotoGallery.propTypes = {
  xs: PropTypes.number,
  sm: PropTypes.number,
  lg: PropTypes.number,
  xl: PropTypes.number,
  xxl: PropTypes.number,
  userId: PropTypes.number,
  extId: PropTypes.number,
  platform: PropTypes.string,
  username: PropTypes.string,
  quantity: PropTypes.number,
  headerMode: PropTypes.bool,
  preloadedImages: PropTypes.array,
  analytics: PropTypes.bool
};

PhotoGallery.defaultProps = {
  xs: 1, // Elements by row for xs screen size and above
  sm: 6, // Elements by row for sm screen size and above
  lg: 3, // Elements by row for lg screen size and above
  xl: 4, // Elements by row for xl screen size and above
  xxl: 4, // Elements by row for xxl screen size and above
  userId: null,
  extId: null,
  username: null,
  platform: 'instragram',
  quantity: 9,
  headerMode: false,
  preloadedImages: [],
  analytics: false
};

export default React.memo(PhotoGallery);
